import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
    $createNodeSelection,
    $createParagraphNode,
    $getSelection,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $setSelection,
    COMMAND_PRIORITY_EDITOR
} from 'lexical';
import {createContext, useContext, useEffect} from 'react';
import * as React from 'react';
import {$createTableNodeWithDimensions} from '../../nodes/TableNode/TableNode';
import {CellContextShape, CellEditorConfig, InsertTableCommandPayload} from './types';
import {INSERT_TABLE_COMMAND} from '../../commands';

export const CellContext: React.Context<CellContextShape | any> = createContext({
    cellEditorConfig: null,
    cellEditorPlugins: null,
    set: () => {}
});

export function TablePlugin({
    cellEditorConfig,
    children
}: {
    cellEditorConfig: CellEditorConfig;
    children?: React.ReactNode | Array<React.ReactNode>;
}) {
    const [editor] = useLexicalComposerContext();
    const cellContext = useContext(CellContext);

    useEffect(() => {
        cellContext.set(cellEditorConfig, children);

        return editor.registerCommand<InsertTableCommandPayload>(
            INSERT_TABLE_COMMAND,
            ({columns, rows, includeHeaders}) => {
                const selection = $getSelection();

                if (!$isRangeSelection(selection)) {
                    return true;
                }

                const focus = selection.focus;
                const focusNode = focus.getNode();

                if (focusNode !== null) {
                    const tableNode = $createTableNodeWithDimensions(
                        Number(rows),
                        Number(columns),
                        includeHeaders
                    );

                    if ($isRootOrShadowRoot(focusNode)) {
                        const target = focusNode.getChildAtIndex(focus.offset);

                        if (target !== null) {
                            target.insertBefore(tableNode);
                        } else {
                            focusNode.append(tableNode);
                        }

                        tableNode.insertBefore($createParagraphNode());
                    } else {
                        const topLevelNode = focusNode.getTopLevelElementOrThrow();
                        topLevelNode.insertAfter(tableNode);
                    }

                    tableNode.insertAfter($createParagraphNode());
                    const nodeSelection = $createNodeSelection();
                    nodeSelection.add(tableNode.getKey());
                    $setSelection(nodeSelection);
                }
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [cellContext, cellEditorConfig, children, editor]);

    return null;
}
